/**
 * The Assigned column, shared by all three Enquiries tables (Event Interest
 * Submissions, B2B Proposals, Report leads). One copy, so the three tables'
 * left-pinned first columns cannot drift apart in width, options or wire key.
 *
 * Lives in a non-component module so the panels that import it keep fast
 * refresh (`only-export-components`).
 */
import type { ColumnFilterOption } from '@/components/ui';
import type {
  EnquiryAssignee,
  EnquiryAssigneeFilter,
  EnquiryAssigneeFilterCsv,
  EnquiryAssignment,
} from '@/lib/types';
import type { ColumnSpec } from '@/lib/use-table-filters';

/** The Assigned column's width. */
export const ENQUIRY_ASSIGNMENT_COLUMN_WIDTH = 210;

/** The list param the Assigned funnel's selections are sent as (CSV). */
export const ASSIGNEE_PARAM = 'assigneeId';

/** The funnel value for an unassigned enquiry — the literal the server takes. */
const UNASSIGNED_VALUE = 'none';

/**
 * The funnel options: Unassigned, then every person on the Settings list.
 * ⚠️ Removed people are INCLUDED and labelled — an enquiry can still be
 * assigned to somebody no longer offered, and that is exactly the row a
 * reader filtering by them is looking for.
 */
export function assigneeFilterOptions(assignees: readonly EnquiryAssignee[]): ColumnFilterOption[] {
  const active = assignees.filter((person) => person.isActive);
  const removed = assignees.filter((person) => !person.isActive);
  const optionOf = (person: EnquiryAssignee): ColumnFilterOption => ({
    value: String(person.id),
    label: `${person.name}${person.team ? ` · ${person.team}` : ''}${person.isActive ? '' : ' (removed)'}`,
  });
  return [
    { value: UNASSIGNED_VALUE, label: 'Unassigned' },
    ...active.map(optionOf),
    ...removed.map(optionOf),
  ];
}

/**
 * The funnel value of a row: the assignee's id, `none` when unassigned, and
 * `null` (empty) when the server did not report the field or the snapshot
 * carries no id — there is nothing to match on, never "Unassigned".
 */
function assignmentFilterValueOf(assignment: EnquiryAssignment | null | undefined): string | null {
  if (assignment === undefined) return null;
  if (assignment === null) return UNASSIGNED_VALUE;
  return assignment.assignee.id === null ? null : String(assignment.assignee.id);
}

/** One funnel value as a filter value: an id, `none`, or `null` when malformed. */
function assigneeValueOf(raw: string): EnquiryAssigneeFilter | null {
  if (raw === UNASSIGNED_VALUE) return UNASSIGNED_VALUE;
  if (!/^[1-9]\d*$/.test(raw)) return null;
  return Number(raw);
}

/**
 * The funnel's server selection as the list param: the valid values (ids and/or
 * `none`) comma-separated, or `null` to omit the key. A STRING so it compares by
 * value — callers key effects and render-time resets on it.
 */
export function assigneeFilterOf(raw: string | undefined): EnquiryAssigneeFilterCsv | null {
  if (raw === undefined || raw === '') return null;
  const values = [
    ...new Set(
      raw
        .split(',')
        .map((part) => part.trim())
        .map(assigneeValueOf)
        .filter((value): value is EnquiryAssigneeFilter => value !== null),
    ),
  ];
  return values.length > 0 ? values.join(',') : null;
}

/**
 * The Assigned column's filter spec, for any table whose row carries
 * `assignment`. SERVER-scoped and `multi` (2026-10-28): every selection goes
 * out as one comma-separated `assigneeId`.
 *
 * ⚠️ With `options` unread (`null`) the hook accumulates values off the loaded
 * rows, so `format` names them from the rows' own snapshots.
 */
export function assignmentColumnSpec<T extends { assignment: EnquiryAssignment | null | undefined }>(
  label: string,
  options: readonly ColumnFilterOption[] | null,
  rows: readonly T[],
): ColumnSpec<T> {
  const names = new Map<string, string>([[UNASSIGNED_VALUE, 'Unassigned']]);
  for (const row of rows) {
    const value = assignmentFilterValueOf(row.assignment);
    if (value !== null && row.assignment) names.set(value, row.assignment.assignee.name);
  }
  return {
    key: 'assignment',
    label,
    get: (row) => assignmentFilterValueOf(row.assignment),
    format: (value) => names.get(value) ?? value,
    scope: 'server',
    param: ASSIGNEE_PARAM,
    multi: true,
    ...(options !== null ? { options: options.map((option) => ({ ...option })) } : {}),
  };
}
